import {useState} from 'react';
import {useSelector} from 'react-redux';

import type {User} from 'nutshell-core/users/users-types';

import {Stack} from 'shells/layout/stack/stack';

import type {AccountPageFragment} from 'nutshell-graphql-types';

import {CustomizeLayoutButton} from '../../../core-entity/layout-customizer/customize-layout-button';
import {MiniActionBar} from '../../../core-entity/mini-header/mini-action-bar';
import {MiniHeader} from '../../../core-entity/mini-header/mini-header';
import {TAB_CALL_MINI_FIXED_ANCHOR} from '../../../core-entity/redux/consts';
import {getCoreEntityPageActionBarTab} from '../../../core-entity/redux/core-entity-page-selectors';
import {
    getTabOptions,
    useHandleCallClick,
    useOpenEmailModal,
    useOpenTextModal,
} from '../../../core-entity/util/action-bar-helpers';
import {PeepAssigneeSelect} from '../../../entity-details/peep-details/peep-assignee-select';
import {PeepMiniHeaderTitle} from '../../../peep/shared/peep-mini-header-title';

import {PeepHeaderActionMenu} from '../../shared/header/action-bar/peep-header-action-menu';

import {AccountContextOverview} from '../header/account-context-overview';

import styles from '../../peep-page.module.css';

type Props = {
    isShown: boolean;
    contentRef: {
        current: HTMLDivElement | null;
    };
    account: AccountPageFragment & {
        hasUploadedAvatar?: boolean | null;
    };
    user: User;
    scrollContainerTop: number;
    onUpdateAssignee: (assigneeId: string | null) => Promise<any>;
    onUpdateDeletedStatus: (isDeleted: boolean) => Promise<any>;
    onHardDelete: () => Promise<any>;
};

export function AccountHeaderMiniOnScroll(props: Props) {
    const [isUpdatingAssignee, setIsUpdatingAssignee] = useState<boolean>(false);

    const actionBarTab = useSelector(getCoreEntityPageActionBarTab);

    const openEmailModal = useOpenEmailModal(props.account);
    const {openTextModal} = useOpenTextModal(props.account, props.user);
    const {handleCallClick, popoverComponent} = useHandleCallClick(
        TAB_CALL_MINI_FIXED_ANCHOR,
        props.account.id,
        {
            noPhoneNumbersQuestion: 'No phone numbers associated with this company',
        }
    );

    const handleUpdateAssignee = (assigneeId?: string | null | undefined) => {
        if (isUpdatingAssignee) {
            return;
        }

        setIsUpdatingAssignee(true);
        props.onUpdateAssignee(assigneeId ?? null).finally(() => {
            setIsUpdatingAssignee(false);
        });
    };

    const options = getTabOptions(openEmailModal, openTextModal, handleCallClick);

    return (
        <MiniHeader
            isShown={props.isShown}
            contentRef={props.contentRef}
            scrollContainerTop={props.scrollContainerTop}
            entityType='peep'
        >
            <Stack spacing={8} isFullWidth={true}>
                <Stack
                    isHorizontal={true}
                    spacing={8}
                    justifyContent='space-between'
                    isFullWidth={true}
                    alignItems='start'
                >
                    <PeepMiniHeaderTitle
                        avatarUrl={props.account.avatarUrl}
                        hasUploadedAvatar={Boolean(props.account.hasUploadedAvatar)}
                        avatarType='accounts'
                        name={props.account.name}
                        contextOverview={
                            <AccountContextOverview account={props.account} isMiniHeader={true} />
                        }
                        hasChildren={props.account.childAccounts.totalCount > 0}
                        isDeleted={props.account.isDeleted ?? false}
                    />
                    <Stack isHorizontal={true} spacing={8} alignItems='center'>
                        <div className={styles['assignee-select-container']}>
                            <PeepAssigneeSelect
                                assigneeId={props.account.owner?.id ?? null}
                                onUpdatePeepAssignee={handleUpdateAssignee}
                                withControlIcon={false}
                                placeholder='Assign this company…'
                                isDisabled={
                                    !props.user.permissions.canAssignEntities || isUpdatingAssignee
                                }
                            />
                        </div>
                        {props.user.permissions.canAccessSetup && (
                            <CustomizeLayoutButton text='Layout' entityType={'ACCOUNT'} />
                        )}
                        <PeepHeaderActionMenu
                            entityType='account'
                            entityId={props.account.id}
                            entityName={props.account.name}
                            isSoftDeleted={props.account.isDeleted ?? false}
                            onDelete={() => props.onUpdateDeletedStatus(true)}
                            onUndelete={() => props.onUpdateDeletedStatus(false)}
                            onPermanentlyDelete={props.onHardDelete}
                        />
                    </Stack>
                </Stack>
                <MiniActionBar
                    entityId={props.account.id}
                    entityName={'Accounts'}
                    entityDisplayName={props.account.name}
                    options={options}
                    selectedTab={actionBarTab}
                    isShown={props.isShown}
                />
                {popoverComponent}
            </Stack>
        </MiniHeader>
    );
}
